import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import lodashGet from "lodash/get";

import UploadIcon from "mdi-material-ui/Upload";
import DownloadIcon from "mdi-material-ui/Download";
import EditIcon from "@material-ui/icons/Edit";

// jaza-soft
import { PageHeader, showNotification, required, translate } from "jazasoft";

import MuiTable from "../../../../components/MuiTable";
import useQuery from "../../../../hooks/useQuery";
import { isEmpty, toFixed, toFixedValue, getDistinctValues, listFromCsv, isTrue } from "../../../../utils/helpers";
import { format } from "../../../../utils/dateUtils";
import useSafeState from "../../../../hooks/useSafeState";
import useMutation from "../../../../hooks/useMutation";
import FormDialog from "../../../../components/FormDialog";
import ExcelErrorDialog from "../../../../components/ExcelErrorDialog";
import MyFileInput from "../../../../components/MyFileInput";
import { useDeepMemo, useSetting, useTime } from "../../../../hooks";
import { CSV, Entity, QcStatus, SyncStatus } from "../../../../utils/types";
import { colorLightGreen, colorOrange, colorTomatoRed, colorSecondary } from "../../../../utils/colors";
import SettingKeys from "../../../../utils/SettingKeys";
import hasPrivilege from "../../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../../utils/privilege";
import { LotType, UOM } from "../../../../constants";
import useWarehouse from "../../../../hooks/useWarehouse";

import RollSplitDrawer from "../po-view/drawers/RollSplitDrawer";
import { sanitizeRoll } from "../../../../helpers/warehouseHelper";
import { convert } from "../../../../helpers/unitConversionHelper";
import { getCutOrderLabel, getGroupOrderLabel } from "../../../../helpers/orderHelper";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const predefinedRollFields = [
  "supplierRollNo",
  "factoryRollNo",
  "length",
  "weight",
  "width",
  "finishedWidth",
  "gsm",
  "rackNo",
  "qcStatus",
  "invStatus",
  "finishedWidth",
  "warpShrPercent",
  "weftShrPercent",
  "warpShrValue",
  "weftShrValue",
  "warpPattern",
  "weftPattern",
  "patternNo",
  "shade",
  "csv",
  "consignment",
  "skewness",
  "skewnessValue",
  "userRemarks"
];

const sanitizeRollInfo = ({
  id,
  rollNo,
  supplierRollNo,
  qcStatus,
  weight,
  length,
  width,
  finishedWidth,
  gsm,
  shade,
  csv,
  warpShrValue,
  weftShrValue,
  warpShrPercent,
  weftShrPercent,
  skewnessValue,
  warpPattern,
  weftPattern,
  patternNo,
  skewness,
  consignment,
  syncStatus,
  userRemarks
}) => ({
  id,
  rollNo,
  supplierRollNo,
  qcStatus,
  weight,
  length,
  width,
  finishedWidth,
  gsm,
  shade,
  csv,
  warpShrValue,
  weftShrValue,
  warpShrPercent,
  weftShrPercent,
  skewnessValue,
  warpPattern,
  weftPattern,
  patternNo,
  skewness,
  consignment,
  syncStatus,
  userRemarks
});

const renderQcStatus = value => (
  <span
    style={{
      backgroundColor:
        value === QcStatus.PASS
          ? colorLightGreen.dark
          : value === QcStatus.FAIL
            ? colorTomatoRed.dark
            : value === QcStatus.ON_HOLD
              ? colorOrange.dark
              : value === QcStatus.PENDING
                ? colorSecondary.dark
                : "none",
      color: "white",
      padding: "4px 8px",
      borderRadius: 4
    }}>
    {value}
  </span>
);

const renderSyncStatus = value => {
  return (
    <span
      style={{
        backgroundColor: [SyncStatus.IN_SYNC, SyncStatus.COMPLETED, SyncStatus.QC_COMPLETED, SyncStatus.PASS].includes(value)
          ? colorLightGreen.dark
          : [SyncStatus.PENDING, SyncStatus.Fail].includes(value)
            ? colorTomatoRed.dark
            : value === SyncStatus.RE_QC
              ? colorOrange.dark
              : "none",
        color: "white",
        padding: "4px 8px",
        borderRadius: 4
      }}>
      {value}
    </span>
  );
};

const footerActions = [
  {
    name: "edit",
    tooltip: "Update",
    icon: <EditIcon />,
    options: { variant: "outlined", color: "primary" }
  },
  {
    name: "download",
    tooltip: "Download",
    icon: <DownloadIcon />,
    options: { variant: "outlined", color: "primary" }
  },
  {
    name: "updateLength",
    tooltip: "Update Stock",
    icon: <UploadIcon />,
    options: { variant: "outlined", color: "primary" }
  },
  {
    name: "updateQc",
    tooltip: "Update QC",
    icon: <UploadIcon />,
    options: { variant: "outlined", color: "primary" }
  }
];

const FabricQualityView = props => {
  const { roles, hasAccess, history } = props;
  const fabricItemId = props.match.params.id;

  const [version, setVersion] = useSafeState(0);
  const [dialogState, setDialogState] = useSafeState({ active: false, action: null });
  const [errorList, setErrorList] = useSafeState([]);
  const [filterValues, setFilterValues] = useSafeState({});
  const [drawerState, setDrawerState] = useSafeState({ open: false, selectedRolls: [], name: null }); // name: 'split'

  const dispatch = useDispatch();
  const [mutate] = useMutation();
  const [download] = useMutation({ rawResponse: true });
  const { utc_offset } = useTime();
  const i18nMessages = useSelector(state => state?.i18n?.messages);
  const { allWhList } = useWarehouse();

  const invoiceFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.INVOICES);
  const fabricFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRICS);
  const fabricItemFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRIC_ITEMS);
  const rollFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ROLLS);
  const wfxWmsIntegrationEnabled = useSetting(SettingKeys.WFX_WMS_INTEGRATION_ENABLED);
  const conversionFactor = useSetting(SettingKeys.UOM_CONVERSION_FACTOR);
  const conversionFactorList = JSON.parse(conversionFactor || "[]");

  const allowedCsvValues = useSetting(SettingKeys.ALLOWED_CSV_VALUES) || "";
  const allowedCsvList = listFromCsv(allowedCsvValues) || [];

  const { data: fabricItem = {} } = useQuery(fabricItemId && { url: `fabric-items/${fabricItemId}`, version });
  const { data: rollList = [] } = useQuery(
    !isEmpty(fabricItemId) && {
      url: "rolls",
      params: { search: `fabricItem.id==${fabricItemId};parentId=='NULL'`, size: 10000 },
      version
    }
  );
  const { data: externalApps = [] } = useQuery({ url: "externalApps" });
  const appList = isEmpty(fabricItem?.invoice?.externalIds)
    ? []
    : externalApps.filter(ea => ea?.integrationOperationList?.some(op => "RollInfo" === op.entityId));

  const orderIds = getDistinctValues(rollList?.map(r => r.orderId).filter(id => !isEmpty(id))) || [];

  const { data: orderList = [] } = useQuery(
    !isEmpty(orderIds) && {
      url: `orders`,
      params: { search: `id=in=(${orderIds.join(",")})`, deep: true }
    }
  );

  const orderFlowList = useDeepMemo(() => {
    if (isEmpty(orderList)) return [];
    return orderList?.flatMap(order => order.orderFlowList.map(of => ({ ...of, order })));
  }, [orderList]);

  const infoColumnList = useDeepMemo(() => {
    const columns = [
      { dataKey: "invoiceNo", title: "Invoice No", linkPath: row => history.push(`/warehouse/invoices/${row.invoiceId}/view`) },
      { dataKey: "erpInvoiceNo", title: "ERP Invoice No" },
      { dataKey: "supplier", title: "Supplier" },
      { dataKey: "itemCode", title: "Item Code" },
      { dataKey: "color", title: "Fabric Color" },
      { dataKey: "itemDesc", title: "Item Description" },
      { dataKey: "articleName", title: "article Name" },
      { dataKey: "fpo", title: "FPO" },
      { dataKey: "rollForm", title: "Roll Form" },
      {
        dataKey: "orderingWidth",
        title: "Booking Width",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomWidth]}`)
      },
      { dataKey: "gsm", title: "Booking GSM" },
      {
        dataKey: "invoiceQty",
        title: "Invoice Qty",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomLength]}`)
      },
      { dataKey: "grn", title: "GRN" },
      { dataKey: "grnDate", title: "GRN Date" },
      {
        dataKey: "grnQty",
        title: "GRN Qty",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomLength]}`)
      },
      { dataKey: "warehouse", title: "Warehouse" }
    ];
    return columns;
  }, []);

  const infoRows = useDeepMemo(() => {
    if (isEmpty(fabricItem)) return [];
    const infoRows = [
      {
        ...fabricItem.invoice,
        ...fabricItem.fabric,
        ...fabricItem,
        invoiceId: fabricItem.invoice?.id,
        grnDate: !isEmpty(utc_offset) && !isEmpty(fabricItem?.grnDate) ? format(fabricItem.grnDate, utc_offset, "dd MMM, yy") : "-",
        supplier: fabricItem.invoice?.supplier?.name,
        warehouse: allWhList?.find(e => e.code === fabricItem.warehouse)?.name
      }
    ];
    return infoRows;
  }, [fabricItem, utc_offset, allWhList]);

  const rows = useDeepMemo(() => {
    if (isEmpty(rollList)) return [];

    const rows = rollList
      .sort((a, b) => (a.rollNo < b.rollNo ? -1 : 1))
      .map(ri => ({
        ...ri,
        allocOrder: orderFlowList?.find(of => of.id === ri.allocOrderId),
        orderFlow: orderFlowList?.find(of => of.id === ri.groupOrderId),
        cutOrderFlow: orderFlowList?.find(of => of.id === ri.cutOrderId)
      }))
      .map(({ parentId, ...roll }) => ({
        ...roll,
        millLength: roll.supplierRoll?.supplierLength,
        availableLength: roll.length,
        syncStatus: roll.extQcStatus === "Completed" && roll.version === roll.syncedVersion ? SyncStatus.IN_SYNC : roll.extQcStatus,
        order: roll.allocOrder?.order?.poRef,
        groupOrder: getGroupOrderLabel(roll.orderFlow, roll.orderFlow?.order, ["poRef"]),
        cutOrder:
          roll.cutOrderFlow?.lotType === LotType.PRE_RUN
            ? getCutOrderLabel(roll.cutOrderFlow, roll.cutOrderFlow?.order, ["poRef", "lot", "inseam"])
            : getCutOrderLabel(roll.cutOrderFlow, roll.cutOrderFlow?.order, ["poRef", "inseam"])
        // syncStatus: !isEmpty(roll.syncedVersion)
        //   ? roll.syncedVersion === 0
        //     ? SyncStatus.NOT_SYNCED
        //     : roll.version === roll.syncedVersion
        //     ? SyncStatus.IN_SYNC
        //     : SyncStatus.NOT_IN_SYNC
        //   : "-"
      }));
    return rows;
  }, [rollList, orderFlowList]);

  const onSubmit = (rows, _, onSubmitComplete) => {
    if (isEmpty(rows)) return;

    const options = {
      url: `rolls`,
      method: "put",
      data: rows.map(sanitizeRollInfo),
      params: { action: "default", fabricItemIds: `${fabricItemId}` }
    };

    mutate(options, {
      onSuccess: () => {
        onSubmitComplete && onSubmitComplete(rows);
        setVersion(v => v + 1);
      },
      onFailure: err => {
        if (err.status === 400 && err.errors && Array.isArray(err.errors)) {
          setErrorList(err.errors.sort((a, b) => a.row - b.row));
        }
      }
    });
  };

  const selectActions = useDeepMemo(() => {
    let actions = [
      {
        name: "sync",
        tooltip: "Sync",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 120 } }
      },
      {
        name: "split",
        tooltip: "Split Roll",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em" } }
      },
      {
        name: "merge",
        tooltip: "Merge Rolls",
        showLabel: true,
        options: { variant: "outlined", color: "primary", style: { marginLeft: "1em" } }
      }
    ];

    //** Syncing QC Details to External System (Manual Button) **//
    if (isEmpty(appList) || wfxWmsIntegrationEnabled !== "false") {
      actions = actions.filter(a => a.name !== "sync");
    }

    return actions;
  }, [appList, wfxWmsIntegrationEnabled]);

  const onUpload = values => {
    // Read Excel //
    const data = new FormData();
    data.append("file", values.file?.rawFile);
    const options = { url: `import/excel`, method: "post", data };
    mutate(options, {
      onSuccess: excelSheetList => {
        if (Array.isArray(excelSheetList) && excelSheetList.length > 0) {
          const excelRowList = excelSheetList[0].excelData?.rowList || [];
          const headerRow = excelRowList[0];
          const headerMap = headerRow.cellList.reduce(
            (acc, c) => ({
              ...acc,
              [`${c.colNo}`]: !isEmpty(c.value) ? `${c.value}`.trim() : c.value || ""
            }),
            {}
          );

          const labelDataKeyMap = predefinedRollFields.reduce(
            (acc, field) => ({
              ...acc,
              [lodashGet(i18nMessages, `resources.rolls.fields.${field}`, field).toUpperCase()]: field
            }),
            {}
          );

          const rollInfoList = excelRowList
            .slice(1)
            .map(row => row.cellList.reduce((acc, c) => ({ ...acc, [labelDataKeyMap[headerMap[`${c.colNo}`]?.toUpperCase()]]: c.value }), {}))
            .filter(r => !isEmpty(r.factoryRollNo) || !isEmpty(r.supplierRollNo));

          //* Update Roll QC/Stock *//
          const hasFactoryRollNo = rollInfoList.some(ri => !isEmpty(ri.factoryRollNo));
          let finalRollList = [];
          if (hasFactoryRollNo) {
            finalRollList = rollInfoList
              .map(ri => ({
                ...ri,
                id: rollList.find(e => `${e.rollNo}` === `${ri.factoryRollNo}`)?.id,
                rollNo: rollList.find(e => `${e.rollNo}` === `${ri.factoryRollNo}`)?.rollNo
              }))
              .filter(ri => !isEmpty(ri.id));
          } else {
            finalRollList = rollInfoList
              .map(ri => ({
                ...ri,
                id: rollList.filter(e => `${e.supplierRollNo}` === `${ri.supplierRollNo}`)[0]?.id,
                rollNo: rollList.filter(e => `${e.supplierRollNo}` === `${ri.supplierRollNo}`)[0]?.rollNo
              }))
              .filter(ri => !isEmpty(ri.id));
          }

          const options = {
            url: `rolls`,
            method: "PUT",
            params: { fabricItemIds: `${fabricItemId}`, operation: dialogState?.action === "updateLength" ? "length" : "qc" },
            data: finalRollList
          };

          mutate(options, {
            onSuccess: () => {
              dispatch(showNotification(`${dialogState.name === "updateQc" ? "QC updated " : "Stock updated"} successfully`));
              setDialogState({ active: false });
              setVersion(v => v + 1);
            },
            onFailure: err => {
              setErrorList(err.errors.sort((a, b) => a.row - b.row));
              setDialogState({ active: false });
            }
          });
        } else {
          setDialogState({ active: false });
        }
      },
      onFailure: err => {
        setDialogState({ active: false });
      }
    });
  };

  const onSelectActionClick = (event, action, selectedRows, onActionComplete) => {
    if (action === "split") {
      setDrawerState({ open: true, selectedRolls: selectedRows, action });
      onActionComplete && onActionComplete(rows);
    } else if (action === "merge") {
      if (selectedRows?.length <= 1) {
        dispatch(showNotification(`At least two split rolls must be selected for merging`, { type: "danger" }));
        onActionComplete && onActionComplete(rows);
        return;
      }
      const rollNumList = getDistinctValues(selectedRows.map(row => row.originalFactoryRollNo));
      if (rollNumList?.length > 1) {
        dispatch(showNotification(`Different roll numbers cannot be merged.`, { type: "danger" }));
        onActionComplete && onActionComplete(rows);
        return;
      }

      const options = {
        url: `rolls/splitting`,
        method: "put",
        data: selectedRows.map(sanitizeRoll),
        params: { action: "merge", fabricItemIds: `${fabricItemId}` }
      };

      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification(`Rolls merged successfully.`));
          setVersion(v => v + 1);
          onActionComplete && onActionComplete(rows);
        },
        onFailure: () => onActionComplete && onActionComplete(rows)
      });
    }
    if (action === "sync") {
      if (selectedRows.filter(e => e.qcStatus === QcStatus.PASS || e.qcStatus === QcStatus.FAIL)?.length < selectedRows?.length) {
        dispatch(showNotification(`Only rolls which are Pass/Fail can be synced. Hint: Select only rolls which are Pass/Fail`, { type: "danger" }));
        onActionComplete && onActionComplete(rows);
        return;
      }
      const syncableRolls = selectedRows.filter(e => isTrue(e.isSplit) || ["Pending", "Re-QC"].includes(e.extQcStatus));
      if (syncableRolls?.length < selectedRows?.length) {
        dispatch(showNotification(`Already synced rolls cannot be synced again. Hint: Send Re-QC request from ERP`, { type: "danger" }));
        onActionComplete && onActionComplete(rows);
        return;
      }
      if (selectedRows.every(row => row.extQcStatus === "Completed")) {
        dispatch(showNotification(`Selected Rolls are already synced.`, { type: "danger" }));
        onActionComplete && onActionComplete(rows);
        return;
      }

      const options = {
        url: `external/rolls/sync-qc`,
        method: "post",
        data: selectedRows.map(({ id, rollNo, supplierRollNo }) => ({ id, rollNo, supplierRollNo }))
      };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification(`Rolls ${action}ed successfully.`));
          setVersion(v => v + 1);
          // onActionComplete && onActionComplete(selectedRows);
        }
        // onFailure: () => onActionComplete && onActionComplete(selectedRows)
      });
      onActionComplete && onActionComplete(rows);
    }
  };

  const onFooterActionClick = (event, action, rows, filterValues, onActionComplete) => {
    if (action === "updateLength" || action === "updateQc") {
      setDialogState({ active: true, action });
    } else if (action === "download") {
      const filename = `ROLLS`.replaceAll(" ", "-").replaceAll("/", "-");

      const isKnitwear = rows.some(row => !isEmpty(row.weight));
      const finalRollFields = [
        ...rollFields.filter(field => field !== "qcStatus").filter(field => isKnitwear || !["weight", "finishedWidth", "gsm"].includes(field)),
        "qcStatus"
      ].filter(field => predefinedRollFields.includes(field));
      const columnList = finalRollFields
        .map((field, idx) => ({ dataKey: field, title: lodashGet(i18nMessages, `resources.rolls.fields.${field}`, field), colNo: idx + 1 }))
        .map(c => ({ ...c, title: c.title?.toUpperCase() }));
      const rollIds = rows
        .filter(row => isEmpty(filterValues?.consignment) || filterValues?.consignment === row.consignment)
        .filter(row => isEmpty(filterValues?.invoiceNo) || filterValues?.invoiceNo === row.invoiceNo)
        .filter(row => isEmpty(filterValues?.lotValue) || filterValues?.lotValue === row.lotValue)
        .filter(row => isEmpty(filterValues?.shade) || filterValues?.shade === row.shade)
        .filter(row => isEmpty(filterValues?.csv) || filterValues?.csv === row.csv)
        .filter(row => isEmpty(filterValues?.qcStatus) || filterValues?.qcStatus === row.qcStatus)
        .map(row => row.id);

      const rowList = rollList
        .filter(roll => rollIds.includes(roll.id))
        .sort((a, b) => b.id - a.id)
        .map(r => ({ ...r, supplierLength: r.supplierRoll?.supplierLength }))
        .map((row, idx) => ({
          rowNo: idx + 1,
          cellList: columnList.map(c => ({
            dataKey: c.dataKey,
            value: row[c.dataKey]
          }))
        }));
      const options = {
        url: `export/excel`,
        method: "post",
        responseType: "arraybuffer",
        params: { filename },
        data: [{ sheetNo: 0, sheetName: "Rolls", excelData: { columnList, rowList } }]
      };

      download(options, {
        onSuccess: response => {
          if (response.status === 200) {
            const header = response.headers["content-disposition"];
            var fileName = header ? header.match(/filename="(.+)"/)[1] : `${filename}.xlsx`;

            let blob = new Blob([response.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
          }
          onActionComplete && onActionComplete(rows);
        }
      });
    }
    onActionComplete && onActionComplete(rows);
  };

  const infoColumns = useDeepMemo(() => {
    let fields = [...invoiceFields, ...fabricFields, ...fabricItemFields];
    if (allWhList?.length > 1) {
      fields.push("warehouse");
    }
    return infoColumnList.filter(c => fields.includes(c.dataKey));
  }, [infoColumnList, invoiceFields, fabricFields, fabricItemFields, allWhList]);
  const columnList = useDeepMemo(() => {
    const isKnitwear = rows.some(row => !isEmpty(row.weight));
    const columns = [
      { dataKey: "supplierRollNo", title: "Supplier Roll No" },
      { dataKey: "rollNo", title: "Roll No" },
      {
        dataKey: "qcStatus",
        title: "QC Status",
        align: "center",
        filterOptions: { filter: true },
        inputType: "select-input",
        choices: Object.values(QcStatus).map(v => ({ id: v, name: v })),
        render: renderQcStatus
      },
      { dataKey: "invStatus", title: "Inv. Status", filterOptions: { filter: true } },
      {
        dataKey: "weight",
        title: "Weight",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "" : `${toFixedValue(value, 3)} kg`)
      },
      {
        dataKey: "length",
        title: "Available\nLength",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (!isEmpty(value) ? `${toFixed(value)} m` : ""),
        disabled: row => !isEmpty(row?.weight)
      },
      {
        dataKey: "finishedWidth",
        title: "Finished\nWidth",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) || value === 0 ? "-" : `${value} cm`)
      },
      {
        dataKey: "gsm",
        title: "GSM",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) || value === 0 ? "-" : value)
      },
      {
        dataKey: "width",
        title: "Cuttable\nWidth",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) || value === 0 ? "-" : `${value} cm`)
      },
      {
        dataKey: "warpShrValue",
        title: "Shrinkage Value\n(Length)",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "-" : value)
      },
      {
        dataKey: "weftShrValue",
        title: "Shrinkage Value\n(Width)",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "-" : value)
      },
      {
        dataKey: "warpShrPercent",
        title: "Shrinkage %\n(Length)",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "-" : `${toFixed(value)}%`)
      },
      {
        dataKey: "weftShrPercent",
        title: "Shrinkage %\n(Width)",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "-" : `${toFixed(value)}%`)
      },
      {
        dataKey: "shade",
        title: "Shade",
        inputType: "text-input",
        render: value => (isEmpty(value) ? "-" : value),
        filterOptions: { filter: true }
      },
      {
        dataKey: "csv",
        title: "CSV",
        inputType: "select-input",
        choices: Object.values(CSV)
          .filter(v => allowedCsvList.includes(v))
          .map(v => ({ id: v, name: v })),
        filterOptions: { filter: true },
        render: value => (isEmpty(value) ? "-" : value)
      },
      {
        dataKey: "skewness",
        title: "Skewness %",
        inputType: "text-input",
        options: { type: "number" },
        render: value => (isEmpty(value) ? "-" : `${toFixed(value)}%`)
      },
      { dataKey: "consignment", title: "Consgt. No", filterOptions: { filter: true } },
      {
        dataKey: "userRemarks",
        title: "Remarks",
        inputType: "text-input",
        render: value => (isEmpty(value) ? "-" : value)
      },
      {
        dataKey: "currWarehouse",
        title: "Current Warehouse",
        align: "center",
        render: value => value || "-",
        filterOptions: { filter: true }
      },
      {
        dataKey: "syncStatus",
        title: "Sync Status",
        align: "center",
        filterOptions: { filter: true },
        options: { disabled: true },
        render: renderSyncStatus
      },
      { dataKey: "order", title: "Order", length: 30, filterOptions: { filter: true } },
      { dataKey: "groupOrder", title: "Group Order", length: 50, filterOptions: { filter: true } },
      { dataKey: "cutOrder", title: "Cut Order", length: 50, filterOptions: { filter: true } },
      {
        dataKey: "returnStatus",
        title: "Return Status",
        align: "center",
        render: value => value || "-",
        filterOptions: { filter: true }
      }
    ];
    let cols = columns.filter(
      c =>
        (!isEmpty(appList) && c.dataKey === "syncStatus") ||
        [...rollFields, "returnStatus", "order", "groupOrder", "cutOrder", "currWarehouse"].includes(c.dataKey)
    );
    if (!isKnitwear) {
      cols = cols.filter(c => !["weight", "gsm", "finishedWidth"].includes(c.dataKey));
    }
    return cols;
  }, [rows, allowedCsvList, appList, rollFields]);

  const finalFooterActions = hasPrivilege(roles, hasAccess, ResourceId.ROLL, Scope.UPDATE)
    ? footerActions
    : footerActions.filter(a => a.name === "download");

  const fileteredRows = rows
    .filter(row => isEmpty(filterValues?.consignment) || filterValues?.consignment === row.consignment)
    .filter(row => isEmpty(filterValues?.shade) || filterValues?.shade === row.shade)
    .filter(row => isEmpty(filterValues?.csv) || filterValues?.csv === row.csv)
    .filter(row => isEmpty(filterValues?.qcStatus) || filterValues?.qcStatus === row.qcStatus);
  const inventoryQty = fileteredRows.reduce((acc, e) => acc + e.length, 0);

  const grnUom = fabricItem?.uomLength;

  const invQtyInGrnUom = useDeepMemo(() => {
    if (grnUom === UOM.KILOGRAM.value) {
      return fileteredRows.reduce((acc, row) => acc + row.weight, 0);
    }
    if (isEmpty(grnUom)) return null;
    return convert({ input: inventoryQty, uomIn: UOM.METER.value, uomOut: grnUom, conversionFactorList });
  }, [fileteredRows, grnUom, conversionFactorList]);

  const title = `Roll Inventory: ${Number(toFixed(inventoryQty, 1))} m${
    grnUom !== UOM.METER.value && !isEmpty(invQtyInGrnUom) ? ` (${toFixedValue(invQtyInGrnUom)} ${UomCodeFromValue[grnUom]})` : ""
  }`;

  const titleSelected = selectedRows => {
    const selectedQty = selectedRows.reduce((acc, e) => acc + e.length, 0);
    const selectedQtyInGrnUom =
      grnUom === UOM.KILOGRAM.value
        ? selectedRows.reduce((acc, e) => acc + e.weight, 0)
        : !isEmpty(grnUom)
          ? convert({ input: selectedQty, uomIn: UOM.METER.value, uomOut: grnUom, conversionFactorList })
          : undefined;

    return `${selectedRows?.length} Rolls: ${toFixed(selectedQty)} m ${
      grnUom !== UOM.METER.value && !isEmpty(selectedQtyInGrnUom) ? ` (${toFixed(selectedQtyInGrnUom)} ${UomCodeFromValue[grnUom]})` : ""
    }  selected`;
  };

  return (
    <div>
      {/** Header Element */}
      <PageHeader title="Fabric - QC" />

      <FormDialog
        maxWidth="sm"
        title="Upload"
        open={dialogState?.active || false}
        onSubmit={onUpload}
        onClose={() => setDialogState({ active: false })}>
        <MyFileInput
          source="file"
          label={"Excel File"}
          accept={{
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "application/vnd.ms-excel": [".xls"]
          }}
          message={translate("js.message.drop_file", { _: "Drag and drop file here, or click to select file" })}
          xs={12}
          fullWidth
          validate={required()}
        />
      </FormDialog>

      <ExcelErrorDialog maxWidth="xl" open={errorList?.length > 0 || false} errors={errorList} onClose={() => setErrorList(null)} />

      <RollSplitDrawer
        open={drawerState.open}
        fabricItemIds={[fabricItemId]}
        selectedRolls={drawerState.selectedRolls || []}
        rollFields={rollFields}
        onRefresh={() => setVersion(v => v + 1)}
        onDrawerClose={() => setDrawerState({ open: false, selectedRolls: [], action: null })}
      />

      <div style={{ margin: "1.5em", marginTop: 0 }}>
        {/* PO Details */}
        <MuiTable title="Item Details" toolbar columns={infoColumns} rows={infoRows} i18nKey="resources.fabricItems.fields" parentIdKey="parentKey" />

        <div style={{ marginTop: "1.5em" }}>
          <MuiTable
            title={title}
            titleSelected={titleSelected}
            columns={columnList}
            rows={rows}
            pageable
            searchable
            editable={hasPrivilege(roles, hasAccess, ResourceId.ROLL, Scope.UPDATE)}
            selectable={hasPrivilege(roles, hasAccess, ResourceId.ROLL, Scope.UPDATE)}
            pageSelect={false}
            searchKeys={["rollNo"]}
            selectActions={selectActions}
            footerActions={finalFooterActions}
            onSelectActionClick={onSelectActionClick}
            onFooterActionClick={onFooterActionClick}
            onSubmit={onSubmit}
            onFilter={setFilterValues}
            fontSize={16}
            pageSize={15}
            rowsPerPageOptions={[15, 25, 100]}
            i18nKey="resources.rolls.fields"
          />
        </div>
      </div>
    </div>
  );
};

export default FabricQualityView;
